"use client";

import { ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function AuditMethodology() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: timelineRef,
    isVisible: timelineVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const steps = [{
    number: "01",
    title: "DATA COLLECTION",
    description: "We begin by gathering comprehensive performance data from your PMS, channel manager, and OTA extranets. Every metric is analyzed to establish a clear baseline of your current market position.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/audit_data_collection.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/audit_data_collection.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/audit_data_collection.png-wvc-sizes",
    alt: "Revenue manager analyzing hotel performance data"
  }, {
    number: "02",
    title: "COMPETITIVE ANALYSIS",
    description: "Our team benchmarks your property against local competitors. We evaluate pricing strategies, room inventory, and digital presence to identify untapped revenue opportunities in your specific market.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/audit_competitive_benchmarking.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/audit_competitive_benchmarking.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/audit_competitive_benchmarking.png-wvc-sizes",
    alt: "Benchmarking hotel rates against local competitors"
  }, {
    number: "03",
    title: "TECH STACK REVIEW",
    description: "We audit your current hospitality technology stack. Our experts assess whether your booking engine, channel manager, and PMS are integrated effectively to maximize direct conversion rates.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/audit_tech_stack.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/audit_tech_stack.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/audit_tech_stack.png-wvc-sizes",
    alt: "Reviewing hotel technology and software integrations"
  }, {
    number: "04",
    title: "STRATEGY FORMULATION",
    description: "Based on our findings, we develop a tailored revenue management roadmap. This includes dynamic pricing recommendations, OTA listing optimizations, and direct booking growth tactics.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/audit_strategy_planning.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/audit_strategy_planning.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/audit_strategy_planning.webp-wvc-sizes",
    alt: "Developing a hotel revenue management strategy"
  }, {
    number: "05",
    title: "IMPLEMENTATION",
    description: "We guide your team through the execution phase. From updating rate plans to refining OTA content, we ensure every strategy is implemented to drive immediate revenue growth.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/audit_implementation_phase.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/audit_implementation_phase.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/audit_implementation_phase.png-wvc-sizes",
    alt: "Implementing revenue management strategies on hotel systems"
  }];
  return <section data-section-id="3823" id="auditmethodology" className="bg-background text-foreground py-20 md:py-28 lg:py-32 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        {}
        <div ref={headerRef} className={`flex flex-col items-center text-center mb-16 md:mb-20 transition-all duration-1000 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="font-default text-xs md:text-sm uppercase tracking-widest text-muted-foreground mb-6">
            § 06 — OUR REVENUE AUDIT PROCESS
          </p>
          <h2 className="font-default font-bold text-5xl md:text-7xl lg:text-8xl uppercase tracking-tight leading-[0.9] text-foreground">
            The Methodology
          </h2>
          <div className="mt-8 h-px w-24" style={{
          background: "var(--secondary)"
        }} aria-hidden="true" />
        </div>

        {}
        <div ref={timelineRef} className="relative">
          {}
          <div className="hidden md:block absolute top-12 left-0 right-0 h-px pointer-events-none z-0">
            <svg className="w-full h-16 -mt-8" viewBox="0 0 1400 60" preserveAspectRatio="none" aria-hidden="true">
              <path d="M 0 30 Q 140 5, 280 30 T 560 30 T 840 30 T 1120 30 T 1400 30" fill="none" stroke="var(--secondary)" strokeWidth="1.5" strokeDasharray="3000" strokeDashoffset={timelineVisible ? "0" : "3000"} style={{
              transition: "stroke-dashoffset 3s ease-out"
            }} />
            </svg>
          </div>

          {}
          <div className="hidden md:block relative z-10">
            <ScrollArea className="w-full">
              <div className="flex gap-6 pb-6 pt-4 snap-x snap-mandatory">
                {steps.map((step, i) => <Card key={step.number} data-index={i} className="snap-start shrink-0 w-[320px] h-[520px] flex flex-col gap-0 py-0 border border-border bg-card/40 backdrop-blur-sm shadow-lg relative overflow-hidden group transition-all duration-500 hover:bg-card/60 hover:border-secondary/40">
                    {}
                    <div className="absolute top-5 left-5 z-20">
                      <span className="font-default font-bold text-[5rem] leading-none tracking-tighter" style={{
                    color: "var(--secondary)"
                  }}>
                        {step.number}
                      </span>
                    </div>

                    {}
                    <div className="px-5 pt-32 pb-4">
                      <div className="relative aspect-square w-full overflow-hidden bg-muted">
                        <img src={step.image} data-wvc-srcset={step.srcset} data-wvc-sizes={step.sizes} alt={step.alt} loading="lazy" decoding="async" className="w-full h-full object-cover grayscale transition-transform duration-700 group-hover:scale-105" />
                      </div>
                    </div>

                    {}
                    <div className="px-5 pb-6 flex flex-col gap-3">
                      <h3 className="font-default font-bold text-xl uppercase tracking-tight text-foreground">
                        {step.title}
                      </h3>
                      <p className="font-default text-base leading-relaxed text-muted-foreground">
                        {step.description}
                      </p>
                    </div>
                  </Card>)}
              </div>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          </div>

          {}
          <div className="md:hidden relative">
            <div className="absolute left-8 top-0 bottom-0 w-px pointer-events-none">
              <svg className="w-8 h-full -ml-4" viewBox="0 0 30 1000" preserveAspectRatio="none" aria-hidden="true">
                <path d="M 15 0 Q 5 100, 15 200 T 15 400 T 15 600 T 15 800 T 15 1000" fill="none" stroke="var(--secondary)" strokeWidth="1.5" strokeDasharray="2000" strokeDashoffset={timelineVisible ? "0" : "2000"} style={{
                transition: "stroke-dashoffset 3s ease-out"
              }} />
              </svg>
            </div>

            <div className="flex flex-col gap-8 relative z-10">
              {steps.map((step, i) => <Card key={step.number} data-index={i} className="ml-12 flex flex-col gap-0 py-0 border border-border bg-card/40 backdrop-blur-sm shadow-md relative overflow-hidden">
                  <div className="absolute top-4 left-4 z-20">
                    <span className="font-default font-bold text-[4rem] leading-none tracking-tighter" style={{
                  color: "var(--secondary)"
                }}>
                      {step.number}
                    </span>
                  </div>

                  <div className="px-5 pt-24 pb-4">
                    <div className="relative aspect-square w-full overflow-hidden bg-muted">
                      <img src={step.image} data-wvc-srcset={step.srcset} data-wvc-sizes={step.sizes} alt={step.alt} loading="lazy" decoding="async" className="w-full h-full object-cover grayscale" />
                    </div>
                  </div>

                  <div className="px-5 pb-6 flex flex-col gap-3">
                    <h3 className="font-default font-bold text-xl uppercase tracking-tight text-foreground">
                      {step.title}
                    </h3>
                    <p className="font-default text-base leading-relaxed text-muted-foreground">
                      {step.description}
                    </p>
                  </div>
                </Card>)}
            </div>
          </div>
        </div>

        {}
        <div className="mt-16 md:mt-20 flex justify-center">
          <Button asChild variant="link" className="font-default text-xs md:text-sm uppercase tracking-widest text-foreground hover:no-underline group p-0 h-auto">
            <Link to="/FreeHotelRevenueAuditNoCostCompleteAnalysisPage">
              <span className="border-b border-foreground/30 group-hover:border-secondary pb-1 transition-colors">
                Request Your Free Revenue Audit
              </span>
              <ArrowUpRight className="ml-2 size-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" style={{
              color: "var(--secondary)"
            }} />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}